import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertService } from 'app/shared/services/alert/alert.service';
import { OrderDocumentsService, Tag } from 'app/shared/services/order-documents/order-documents.service';
import { DocumentDialogData } from '../order-documents.component';
import { AutocompleteTag } from 'app/shared/components/tags-autocomplete/tags-autocomplete/tags-autocomplete.component';
import { atLeastOneTagValidator } from 'app/shared/helpers/form.helpers';
import moment from 'moment';

const MAX_FILE_SIZE = 10000000;
@Component({
  selector: 'app-dialog-upload-document',
  templateUrl: './dialog-upload-document.component.html',
  styleUrls: ['./dialog-upload-document.component.scss']
})
export class DialogUploadDocumentComponent implements OnInit {
  uploadDocumentForm: FormGroup;
  allAvailableTags: Tag[] = [];
  loading: boolean = false;
  defaultTags: AutocompleteTag[] = [];
  uploadSuccess = false;

  constructor(private formBuilder: FormBuilder,
    private alertService: AlertService,
    private documentService: OrderDocumentsService,
    @Inject(MAT_DIALOG_DATA) public data: DocumentDialogData,
    private dialogRef: MatDialogRef<DialogUploadDocumentComponent>,
  ) { }

  ngOnInit(): void {
    this.allAvailableTags = this.data.tags;
    this.defaultTags = this.data.defaultTagIds.map(tagId => ({ id: tagId, name: this.allAvailableTags.find(tag => tag.id === tagId).name }));
    this.uploadDocumentForm = this.formBuilder.group({
      document_name: ['', Validators.required],
      tags: [''],
      custom_tags: [[]],
      expiry_date: [null],
      document: ['', Validators.required],
    }, { validator: atLeastOneTagValidator() });

    if (this.data?.defaultTitle) {
      this.uploadDocumentForm.patchValue({ document_name: this.data.defaultTitle });
    }

    if (this.data.options?.disableDocumentTitleInUploadDialog) {
      this.uploadDocumentForm.get('document_name').disable();
    } else {
      this.uploadDocumentForm.get('document_name').enable();
    }

    if (this.data.options?.disableTagAutocompleteInUploadDialog) {
      this.data.options.tagsAutocompleteOptions = { ...this.data.options.tagsAutocompleteOptions, disabled: true }
    }
  }

  uploadFile(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];

    if (file.size > MAX_FILE_SIZE) {
      this.alertService.notifyError('Sorry, the file you tried to upload was too large. Please try again.');

      this.resetFile(input);
      return;
    }
    this.uploadDocumentForm.patchValue({ document: file });
  }

  resetFile(input: HTMLInputElement | undefined = undefined) {
    this.uploadDocumentForm.patchValue({ document: '' });
    if (input) {
      input.value = '';
    }
  }

  onSelectedTagsChanged(selectedTags: AutocompleteTag[]): void {
    const tags = selectedTags.filter(tag => !tag?.custom).map(tag => tag.id).join(',');
    const custom_tags = selectedTags.filter(tag => tag?.custom).map(tag => ({ name: tag.name }));
    this.uploadDocumentForm.patchValue({ tags, custom_tags })
  }

  uploadDocument() {

    if (this.uploadDocumentForm.valid) {
      this.loading = true;
      const data = {
        ...this.uploadDocumentForm.getRawValue(),
        user_id: this.data.userId,
      }
      if (this.data.orderId) {
        data.order_id = this.data.orderId;
      }

      if (data.expiry_date) {
        data.expiry_date = moment(data.expiry_date).format('DD/MM/YYYY');
      }

      this.documentService.createDocument(data).subscribe(
        () => {
          this.loading = false;
          this.uploadSuccess = true;
        },
        () => {
          this.loading = false;
          this.alertService.notifyError('Failed to upload document. Please try again.');
        }
      );
    } else {
      this.uploadDocumentForm.markAllAsTouched();
    }
  }

  isFieldHasRequiredError(fieldName: string): boolean {
    const control = this.uploadDocumentForm.get(fieldName);
    return control?.errors?.required && control?.touched;
  }

  resetFormForNextUpload(): void {
    this.uploadSuccess = false;
    this.uploadDocumentForm.reset();
    this.uploadDocumentForm.patchValue({ tags: '', custom_tags: [] });
    if (this.data?.defaultTitle) {
      this.uploadDocumentForm.patchValue({ document_name: this.data.defaultTitle });
    }
  }

}
